import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  Bell,
  Boxes,
  BrainCircuit,
  Building2,
  Calendar,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  Clock,
  Cpu,
  ExternalLink,
  Eye,
  FileText,
  Globe,
  Headphones,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  UserRound,
  Menu,
  Megaphone,
  Network,
  Newspaper,
  Package,
  Sparkles,
  ScanLine,
  Search,
  Settings,
  ShieldCheck,
  ShoppingBag,
  Store,
  Upload,
  Users,
  Wallet,
  Workflow,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { ConfirmDialog, Spinner } from '@terra/ui';

import { api } from '../lib/api';
import { STOREFRONT_URL } from '../lib/appUrls';
import { isRouteHidden, useModulesStore } from '../lib/modules';
import { isAdmin, useAuthStore } from '../stores/authStore';

import { ErrorBoundary } from './ErrorBoundary';

// ── Icon registry ─────────────────────────────────────────────────────────────

const ICONS: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  products: Package,
  notes: FileText,
  vendors: Store,
  orders: ShoppingBag,
  settlements: Wallet,
  wallet: Wallet,
  crm: Users,
  notifications: Bell,
  support: Headphones,
  erp: Workflow,
  pos: ScanLine,
  b2b: Building2,
  saas: Boxes,
  analytics: BarChart3,
  intelligence: BrainCircuit,
  ai: Cpu,
  staff: Users,
  network: Network,
  platform: LayoutGrid,
  scale: ShieldCheck,
  globalization: Globe,
  settings: Settings,
  upload: Upload,
  blog: Newspaper,
  marketing: Megaphone,
  sponsored: Sparkles,
};

// ── Navigation model ─────────────────────────────────────────────────────────

interface NavLeaf {
  to: string;
  label: string;
  icon?: string;
  end?: boolean;
  badgeKey?: string;
}
interface NavGroup {
  key: string;
  label: string;
  icon: string;
  children: NavEntry[];
}
type NavEntry = NavLeaf | NavGroup;
interface NavSection {
  title: string;
  items: NavEntry[];
}

const isGroup = (e: NavEntry): e is NavGroup => 'children' in e;

const NAV: NavSection[] = [
  {
    title: 'Overview',
    items: [{ to: '/', label: 'Dashboard', icon: 'dashboard', end: true }],
  },
  {
    title: 'Catalog',
    items: [
      {
        key: 'products',
        label: 'Products',
        icon: 'products',
        children: [
          { to: '/products/new', label: 'Add New Product' },
          { to: '/products/all', label: 'All Products' },
          { to: '/products/in-house', label: 'In-House Products' },
          { to: '/products/digital', label: 'Digital Products' },
          {
            key: 'seller-products',
            label: 'Seller Product',
            icon: 'vendors',
            children: [
              { to: '/products/seller/physical', label: 'Physical Product' },
              { to: '/products/seller/digital', label: 'Digital Product' },
            ],
          },
          { to: '/products/bulk-import', label: 'Bulk Import' },
          { to: '/products/bulk-export', label: 'Bulk Export' },
          { to: '/products/category', label: 'Category' },
          { to: '/products/category-discount', label: 'Category Based Discount' },
          {
            key: 'brands',
            label: 'Brand',
            icon: 'saas',
            children: [
              { to: '/products/brand/all', label: 'All Brand' },
              { to: '/products/brand/bulk-import', label: 'Bulk Import Brand' },
            ],
          },
          { to: '/products/attribute', label: 'Attribute' },
          { to: '/products/colors', label: 'Colors' },
        ],
      },
      {
        key: 'notes',
        label: 'Notes',
        icon: 'notes',
        children: [
          { to: '/notes/new', label: 'Add New Note' },
          { to: '/notes', label: 'Note List' },
        ],
      },
      {
        key: 'orders',
        label: 'Orders',
        icon: 'orders',
        children: [
          { to: '/orders/all', label: 'All Orders' },
          { to: '/orders/inhouse', label: 'Inhouse Orders' },
          { to: '/orders/seller', label: 'Seller Orders' },
          { to: '/orders/pickup', label: 'Pickup Point Orders' },
          { to: '/orders/unpaid', label: 'Unpaid Orders' },
          { to: '/orders/returns', label: 'Returns' },
        ],
      },
      {
        key: 'customers',
        label: 'Customers',
        icon: 'crm',
        children: [
          { to: '/customers', label: 'Customer List' },
          { to: '/customers/classified-products', label: 'Classified Products' },
          { to: '/customers/classified-packages', label: 'Classified Packages' },
        ],
      },

      {
        key: 'agents',
        label: 'Agents',
        icon: 'crm',
        children: [
          { to: '/agents/new', label: 'Agent Registration' },
          { to: '/agents', label: 'All Agents', end: true },
          { to: '/agents/partners', label: 'Commerce Partners' },
          { to: '/agents/payout-time', label: 'Agent Payouts' },
        ],
      },
      { to: '/uploaded-files', label: 'Uploaded Files', icon: 'upload' },
      {
        key: 'reports',
        label: 'Reports',
        icon: 'analytics',
        children: [
          { to: '/reports/earnings', label: 'Earning Report' },
          { to: '/reports/in-house-sales', label: 'In House Product Sale' },
          { to: '/reports/seller-sales', label: 'Seller Products Sale' },
          { to: '/reports/stock', label: 'Products Stock' },
          { to: '/reports/wishlist', label: 'Products Wishlist' },
          { to: '/reports/searches', label: 'User Searches' },
          { to: '/reports/commissions', label: 'Commission History' },
          { to: '/reports/wallet-recharges', label: 'Wallet Recharge History' },
        ],
      },
      {
        key: 'blog',
        label: 'Blog System',
        icon: 'blog',
        children: [
          { to: '/blog', label: 'All Posts', end: true },
          { to: '/blog/categories', label: 'Categories' },
        ],
      },
      {
        key: 'marketing',
        label: 'Marketing',
        icon: 'marketing',
        children: [
          { to: '/marketing/banners', label: 'Homepage Banners' },
          { to: '/marketing/flash-deals', label: 'Flash deals' },
          { to: '/marketing/seller-offers', label: 'Seller offers' },
          { to: '/marketing/coupons', label: 'Coupons' },
          { to: '/marketing/dynamic-popup', label: 'Dynamic Pop-up' },
          { to: '/marketing/custom-alert', label: 'Custom Alert' },
          { to: '/marketing/emails', label: 'Emails & Newsletters' },
          { to: '/marketing/notification', label: 'Notification' },
          { to: '/marketing/subscribers', label: 'Subscribers' },
        ],
      },
      {
        key: 'sponsored',
        label: 'Sponsored Control Center',
        icon: 'sponsored',
        children: [
          { to: '/sponsored', label: 'Campaign Dashboard', end: true },
          { to: '/sponsored/active', label: 'Active Campaigns' },
          { to: '/sponsored/draft', label: 'Draft Campaigns' },
          { to: '/sponsored/scheduled', label: 'Scheduled Campaigns' },
          { to: '/sponsored/expired', label: 'Expired Campaigns' },
          { to: '/sponsored/new', label: 'Create Campaign' },
          { to: '/sponsored/analytics', label: 'Analytics' },
          { to: '/sponsored/placements', label: 'Placement Management' },
        ],
      },
      {
        key: 'support',
        label: 'Support',
        icon: 'support',
        children: [
          { to: '/support', label: 'Ticket Queue', end: true, badgeKey: 'tickets' },
          { to: '/support/monitoring', label: 'Monitoring Console' },
          { to: '/support/product', label: 'Product Support' },
          { to: '/support/contacts', label: 'Contacts' },
        ],
      },
      {
        key: 'website-setup',
        label: 'Website Setup',
        icon: 'globalization',
        children: [
          { to: '/website/homepage-settings', label: 'Homepage Settings' },
          { to: '/website/homepage-content', label: 'Homepage Content' },
          { to: '/website/homepage-layout', label: 'Homepage Layout' },
          { to: '/website/festival-theme', label: 'Festival Theme' },
          {
            to: '/website/authentication-layout-settings',
            label: 'Authentication Layout & Settings',
          },
          { to: '/website/header', label: 'Header' },
          { to: '/website/footer', label: 'Footer' },
          { to: '/website/pages', label: 'Pages' },
        ],
      },
      {
        key: 'setup-configurations',
        label: 'Setup & Configurations',
        icon: 'settings',
        children: [
          { to: '/setup/staffs', label: 'Staffs' },
          { to: '/setup/rbac', label: 'Roles & Permissions' },
          { to: '/setup/audit-logs', label: 'Audit Logs' },
          { to: '/setup/features', label: 'Features activation' },
          { to: '/setup/languages', label: 'Languages' },
          { to: '/setup/currency', label: 'Currency' },
          { to: '/setup/tax', label: 'Vat & TAX' },
          { to: '/setup/smtp', label: 'SMTP Settings' },
          { to: '/setup/api-settings', label: 'API Settings' },
          { to: '/setup/payment-methods', label: 'Payment Methods' },
          { to: '/setup/orders', label: 'Order Configuration' },
          { to: '/setup/wallet', label: 'Wallet at Checkout' },
          { to: '/setup/shipping', label: 'Shipping' },
        ],
      },
    ],
  },
  {
    title: 'Marketplace',
    items: [
      {
        key: 'sellers',
        label: 'Sellers',
        icon: 'vendors',
        children: [
          { to: '/sellers', label: 'Sellers', end: true },
          { to: '/sellers/ratings', label: 'Seller Rating & Followers' },
        ],
      },
    ],
  },
  {
    title: 'Finance',
    items: [
      { to: '/payouts', label: 'Settlements', icon: 'settlements' },
      { to: '/wallet', label: 'Wallet', icon: 'wallet' },
    ],
  },
  {
    title: 'Engagement',
    items: [
      { to: '/crm', label: 'CRM', icon: 'crm' },
      { to: '/notifications', label: 'Notifications', icon: 'notifications' },
      { to: '/notifications/delivery', label: 'Delivery Dashboard' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { to: '/erp', label: 'ERP', icon: 'erp' },
      { to: '/pos', label: 'POS', icon: 'pos' },
      { to: '/logistics', label: 'Logistics', icon: 'orders' },
      { to: '/b2b', label: 'B2B', icon: 'b2b' },
      { to: '/saas', label: 'Vendor SaaS', icon: 'saas' },
    ],
  },
  {
    title: 'Intelligence',
    items: [
      { to: '/analytics', label: 'Analytics', icon: 'analytics' },
      { to: '/intelligence', label: 'Intelligence', icon: 'intelligence' },
      { to: '/ai', label: 'AI engines', icon: 'ai' },
      ...(import.meta.env.VITE_FEATURE_MLM_ENABLED === 'true'
        ? [{ to: '/network', label: 'Network & MLM', icon: 'network' }]
        : []),
    ],
  },
  {
    title: 'System',
    items: [
      { to: '/platform', label: 'Platform', icon: 'platform' },
      { to: '/scale', label: 'Scale & security', icon: 'scale' },
      { to: '/globalization', label: 'Globalization', icon: 'globalization' },
      { to: '/settings', label: 'Settings', icon: 'settings' },
    ],
  },
];

/** Collect every route under an entry so we can auto-open the group holding the active page. */
function groupContainsPath(group: NavGroup, path: string): boolean {
  return group.children.some((c) => (isGroup(c) ? groupContainsPath(c, path) : c.to === path));
}

/** The first concrete route under an entry — what decides which module owns it. */
function entryRoute(entry: NavEntry): string {
  if (!isGroup(entry)) return entry.to;
  const first = entry.children[0];
  return first ? entryRoute(first) : '';
}

/**
 * Drop top-level entries whose module the admin switched off in
 * Settings → Modules. Only top-level entries are considered: a module is
 * hidden whole, never one page inside it.
 */
function withoutHiddenModules(
  sections: NavSection[],
  toggles: Parameters<typeof isRouteHidden>[0],
): NavSection[] {
  return sections
    .map((s) => ({ ...s, items: s.items.filter((e) => !isRouteHidden(toggles, entryRoute(e))) }))
    .filter((s) => s.items.length > 0);
}

/** Filter the nav tree by a search query (matches labels; keeps groups with matching descendants). */
function filterEntry(entry: NavEntry, q: string): NavEntry | null {
  if (!isGroup(entry)) {
    return entry.label.toLowerCase().includes(q) ? entry : null;
  }
  if (entry.label.toLowerCase().includes(q)) return entry;
  const kids = entry.children
    .map((c) => filterEntry(c, q))
    .filter((c): c is NavEntry => c !== null);
  return kids.length > 0 ? { ...entry, children: kids } : null;
}

function NavIcon({
  name,
  className,
}: {
  name?: string | undefined;
  className?: string | undefined;
}) {
  const Cmp = (name && ICONS[name]) || LayoutDashboard;
  return <Cmp className={className ?? 'h-[18px] w-[18px]'} strokeWidth={1.9} aria-hidden />;
}

// ── Recursive nav renderer ───────────────────────────────────────────────────

function NavTree({
  entry,
  depth,
  open,
  toggle,
  path,
  searching,
  badges,
}: {
  entry: NavEntry;
  depth: number;
  open: Set<string>;
  toggle: (key: string) => void;
  path: string;
  searching: boolean;
  badges: Record<string, number>;
}) {
  // ── Leaf link ──────────────────────────────────────────────────────────────
  if (!isGroup(entry)) {
    return (
      <NavLink
        to={entry.to}
        end={entry.end ?? false}
        className={({ isActive }) =>
          `group relative flex items-center gap-3 no-underline transition-all duration-200 ${
            depth === 0
              ? 'rounded-xl px-3 py-2.5 text-sm'
              : depth === 1
                ? 'rounded-lg py-2 pl-[44px] pr-3 text-[13px]'
                : 'rounded-lg py-2 pl-[60px] pr-3 text-[13px]'
          } ${
            isActive
              ? 'bg-white/[0.06] font-semibold text-white'
              : `font-medium ${depth === 0 ? 'text-zone-300' : 'text-zone-400'} hover:bg-white/[0.04] hover:text-white`
          }`
        }
      >
        {({ isActive }) => (
          <>
            {/* Active accent bar */}
            <span
              className={`bg-gold-500 absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full transition-opacity duration-200 ${
                isActive ? 'opacity-100' : 'opacity-0'
              }`}
            />
            {depth === 0 ? (
              <NavIcon
                name={entry.icon}
                className={`h-[19px] w-[19px] shrink-0 transition-colors duration-200 ${
                  isActive ? 'text-gold-400' : 'text-zone-400 group-hover:text-white'
                }`}
              />
            ) : (
              <span
                className={`h-1.5 w-1.5 shrink-0 rounded-full transition-colors ${
                  isActive ? 'bg-gold-400' : 'border-zone-500 group-hover:border-zone-400 border'
                }`}
              />
            )}
            <span className="flex-1 truncate">{entry.label}</span>
            {entry.badgeKey && (badges[entry.badgeKey] ?? 0) > 0 && (
              <span className="ml-auto grid h-5 min-w-[20px] place-items-center rounded-md bg-red-500 px-1.5 text-[10px] font-bold leading-none text-white">
                {badges[entry.badgeKey]}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  }

  // ── Group ──────────────────────────────────────────────────────────────────
  const expanded = searching || open.has(entry.key);
  const active = groupContainsPath(entry, path);

  return (
    <div>
      <button
        type="button"
        onClick={() => toggle(entry.key)}
        className={`group flex w-full items-center gap-3 transition-all duration-200 ${
          depth === 0
            ? 'rounded-xl px-3 py-2.5 text-sm'
            : 'rounded-lg py-2 pl-[44px] pr-3 text-[13px]'
        } ${
          active
            ? 'font-semibold text-white'
            : `font-medium ${depth === 0 ? 'text-zone-300' : 'text-zone-400'} hover:bg-white/[0.04] hover:text-white`
        } ${expanded && depth === 0 ? 'bg-white/[0.03]' : ''}`}
      >
        {depth === 0 ? (
          <NavIcon
            name={entry.icon}
            className={`h-[19px] w-[19px] shrink-0 transition-colors duration-200 ${
              active ? 'text-gold-400' : 'text-zone-400 group-hover:text-white'
            }`}
          />
        ) : (
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${active ? 'bg-gold-400' : 'border-zone-500 border'}`}
          />
        )}
        <span className="flex-1 truncate text-left">{entry.label}</span>
        <ChevronDown
          className={`text-zone-500 h-4 w-4 shrink-0 transition-transform duration-300 ${expanded ? '' : '-rotate-90'}`}
          strokeWidth={2.2}
        />
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="overflow-hidden">
          <div className="mt-0.5 space-y-0.5 py-0.5">
            {entry.children.map((child) => (
              <NavTree
                key={isGroup(child) ? child.key : child.to}
                entry={child}
                depth={depth + 1}
                open={open}
                toggle={toggle}
                path={path}
                searching={searching}
                badges={badges}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page title resolver ──────────────────────────────────────────────────────

function resolveTitle(path: string): string {
  const walk = (entries: NavEntry[]): string | undefined => {
    for (const e of entries) {
      if (isGroup(e)) {
        const found = walk(e.children);
        if (found) return found;
      } else if (path === e.to) {
        return e.label;
      }
    }
    return undefined;
  };
  return (
    NAV.flatMap((s) => walk(s.items) ?? []).find(Boolean) ?? (path === '/' ? 'Dashboard' : 'Admin')
  );
}

/** Resolve which top-level section a route belongs to (for the breadcrumb). */
function resolveSection(path: string): string {
  for (const section of NAV) {
    const walk = (entries: NavEntry[]): boolean =>
      entries.some((e) => (isGroup(e) ? walk(e.children) : e.to === path));
    if (walk(section.items)) return section.title;
  }
  return 'Overview';
}

// ── Alerts Bell ──────────────────────────────────────────────────────────────

const ALERTS_READ_KEY = 'terra_admin_alerts_read_at';

interface AlertRow {
  label: string;
  count: number;
  to: string;
  color: string;
  dot: string;
}

/**
 * The signed-in admin, top right. Was a static chip with a sign-out button and
 * no way to reach one's own account — there was no such page. Now a menu:
 * the picture (or initial), the name, and where to go to change any of it.
 */
function ProfileMenu({
  user,
  onLogout,
}: {
  user: {
    name?: string | null;
    email?: string | null;
    avatarUrl?: string | null | undefined;
  } | null;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initial = (user?.name ?? user?.email ?? 'A').slice(0, 1).toUpperCase();
  const email = user?.email ?? '';
  const trimmedEmail = email.length > 24 ? `${email.slice(0, 21)}…` : email;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="border-border hover:bg-surface-subtle flex items-center gap-2 rounded-xl border py-1.5 pl-1.5 pr-2 transition-colors"
      >
        <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-lg bg-red-500 text-sm font-bold text-white">
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            initial
          )}
        </span>
        <span className="hidden min-w-0 text-left leading-tight sm:block">
          <span className="text-ink block max-w-[160px] truncate text-xs font-semibold">
            {user?.name ?? 'Administrator'}
          </span>
          <span className="text-ink-muted block max-w-[160px] truncate text-[11px]">
            {trimmedEmail}
          </span>
        </span>
        <ChevronDown
          className={`text-ink-muted h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="border-border/60 bg-surface absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-xl border shadow-lg"
        >
          <div className="border-border/60 border-b px-3.5 py-3">
            <p className="text-ink truncate text-[13px] font-bold">
              {user?.name ?? 'Administrator'}
            </p>
            <p className="text-ink-muted truncate text-[11.5px]">{email}</p>
          </div>
          <NavLink
            to="/account"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="text-ink hover:bg-surface-subtle flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] font-semibold"
          >
            <UserRound className="text-ink-muted h-4 w-4" /> My account
          </NavLink>
          <NavLink
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="text-ink hover:bg-surface-subtle flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] font-semibold"
          >
            <Settings className="text-ink-muted h-4 w-4" /> Platform settings
          </NavLink>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="border-border/60 flex w-full items-center gap-2.5 border-t px-3.5 py-2.5 text-[13px] font-semibold text-red-600 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function AlertsBell({ status }: { status: 'authenticated' | string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const alertsQ = useQuery({
    queryKey: ['admin-alerts-summary'],
    queryFn: () => api.notifications.alertsSummary(),
    enabled: status === 'authenticated',
    staleTime: 45_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const data = alertsQ.data;

  // This admin's own inbox: seller applications and anything else addressed
  // to them, which the counts above cannot say ("which seller? when?").
  const qc = useQueryClient();
  const navigate = useNavigate();
  const inboxUnread = useQuery({
    queryKey: ['admin-inbox-unread'],
    queryFn: () => api.notifications.myUnreadCount(),
    enabled: status === 'authenticated',
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const inbox = useQuery({
    queryKey: ['admin-inbox'],
    queryFn: () => api.notifications.myList({ pageSize: 8 }),
    enabled: status === 'authenticated' && open,
  });
  const markInboxRead = useMutation({
    mutationFn: (id: string) => api.notifications.markRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-inbox'] });
      void qc.invalidateQueries({ queryKey: ['admin-inbox-unread'] });
    },
  });
  const unreadInbox = inboxUnread.data ?? 0;
  const total = (data?.total ?? 0) + unreadInbox;

  // Red dot: show only when total > 0 AND fetched data is newer than last-read timestamp
  const hasUnread = (() => {
    if (unreadInbox > 0) return true;
    if (total === 0) return false;
    const readAt = localStorage.getItem(ALERTS_READ_KEY);
    if (!readAt || !data?.fetchedAt) return total > 0;
    return new Date(data.fetchedAt) > new Date(readAt);
  })();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    setOpen((v) => !v);
    // Mark as read when opening
    if (!open && data?.fetchedAt) {
      localStorage.setItem(ALERTS_READ_KEY, data.fetchedAt);
    }
  };

  const markRead = () => {
    localStorage.setItem(ALERTS_READ_KEY, new Date().toISOString());
    setOpen(false);
  };

  const rows: AlertRow[] = [
    {
      label: 'Pending vendor approvals',
      count: data?.pendingVendors ?? 0,
      to: '/sellers?status=PENDING_APPROVAL',
      color: 'text-amber-600',
      dot: 'bg-amber-500',
    },
    {
      label: 'Products awaiting review',
      count: data?.pendingProducts ?? 0,
      // The approvals queue; it opens on PENDING_APPROVAL.
      to: '/products',
      color: 'text-blue-600',
      dot: 'bg-blue-500',
    },
    {
      label: 'Unpaid orders',
      count: data?.pendingOrders ?? 0,
      to: '/orders/unpaid',
      color: 'text-purple-600',
      dot: 'bg-purple-500',
    },
    {
      label: 'Return requests',
      count: data?.pendingReturns ?? 0,
      to: '/orders/returns',
      color: 'text-orange-600',
      dot: 'bg-orange-500',
    },
    {
      label: 'Open support tickets',
      count: data?.openTickets ?? 0,
      to: '/support',
      color: 'text-red-600',
      dot: 'bg-red-500',
    },
  ].filter((r) => r.count > 0);

  return (
    <div className="relative" ref={ref}>
      <button
        id="admin-alerts-bell"
        type="button"
        onClick={handleOpen}
        className={`border-border text-ink-muted relative grid h-10 w-10 place-items-center rounded-xl border transition-all duration-200 ${
          open
            ? 'border-gold-300 text-gold-700 bg-gold-50'
            : 'hover:border-gold-300 hover:text-gold-700'
        }`}
        aria-label="Admin alerts"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Bell className="h-[18px] w-[18px]" />
        {hasUnread && (
          <span className="ring-surface absolute right-1.5 top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white ring-2">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="border-border bg-surface shadow-zone-lift absolute right-0 top-[calc(100%+8px)] z-50 w-80 overflow-hidden rounded-2xl border"
            role="dialog"
            aria-label="Alert notifications"
          >
            {/* Header */}
            <div className="border-border flex items-center justify-between border-b px-4 py-3">
              <div className="flex items-center gap-2">
                <Bell className="text-gold-600 h-4 w-4" />
                <span className="text-ink text-sm font-semibold">Admin Alerts</span>
                {total > 0 && (
                  <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                    {total}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={markRead}
                className="text-ink-muted hover:text-ink flex items-center gap-1 text-[11px] font-medium transition-colors"
                title="Mark all as read"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark read
              </button>
            </div>

            {/* This admin's inbox */}
            {(inbox.data?.data ?? []).length > 0 && (
              <ul className="border-border max-h-56 overflow-y-auto border-b">
                {(inbox.data?.data ?? []).map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!n.isRead) markInboxRead.mutate(n.id);
                        setOpen(false);
                        if (n.refType === 'Vendor' && n.refId) {
                          navigate(`/sellers?tab=compliance&review=${n.refId}`);
                        }
                      }}
                      className={`hover:bg-surface-subtle w-full border-b px-4 py-2.5 text-left last:border-0 ${
                        n.isRead ? '' : 'bg-amber-50/60'
                      }`}
                    >
                      <p className="text-ink flex items-center gap-2 text-[13px] font-semibold">
                        {!n.isRead && (
                          <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" aria-hidden />
                        )}
                        {n.title}
                      </p>
                      <p className="text-ink-muted mt-0.5 text-[11.5px] leading-snug">{n.body}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Alert rows */}
            <div className="max-h-72 overflow-y-auto">
              {rows.length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
                  <CheckCheck className="h-8 w-8 text-emerald-500" />
                  <p className="text-ink text-sm font-semibold">All clear</p>
                  <p className="text-ink-muted text-xs">No pending actions right now.</p>
                </div>
              ) : (
                <nav>
                  {rows.map((row) => (
                    <NavLink
                      key={row.to}
                      to={row.to}
                      onClick={() => setOpen(false)}
                      className="border-border hover:bg-surface-subtle flex items-center gap-3 border-b px-4 py-3 transition-colors last:border-0"
                    >
                      <span className={`h-2 w-2 shrink-0 rounded-full ${row.dot}`} />
                      <span className="text-ink-muted flex-1 text-sm">{row.label}</span>
                      <span className={`text-sm font-bold tabular-nums ${row.color}`}>
                        {row.count}
                      </span>
                      <ArrowRight className="text-ink-muted h-3.5 w-3.5 shrink-0" />
                    </NavLink>
                  ))}
                </nav>
              )}
            </div>

            {/* Footer */}
            {alertsQ.isFetching && (
              <div className="border-border border-t px-4 py-2">
                <p className="text-ink-muted text-center text-[10px]">Refreshing…</p>
              </div>
            )}
            {!alertsQ.isFetching && data?.fetchedAt && (
              <div className="border-border border-t px-4 py-2">
                <p className="text-ink-muted text-center text-[10px]">
                  Updated {new Date(data.fetchedAt).toLocaleTimeString()}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Header live Date & Time widget ───────────────────────────────────────────

function LiveDateTimeWidget() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const timeString = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });

  const dateString = now.toLocaleDateString('en-US', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });

  const yearString = now.getFullYear();

  return (
    <div className="hidden items-center gap-2 sm:flex">
      <div className="border-border/70 bg-surface/90 hover:border-gold-500/40 shadow-zone-sm flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs backdrop-blur-md transition-all duration-200">
        {/* Live Pulse Indicator */}
        <span className="relative flex h-2 w-2 shrink-0" title="Live Clock">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>

        {/* Date (visible on md+) */}
        <div className="border-border/60 text-ink-muted hidden items-center gap-1.5 border-r pr-2.5 font-medium md:flex">
          <Calendar className="text-gold-600 h-3.5 w-3.5 shrink-0" />
          <span>
            {dateString}, {yearString}
          </span>
        </div>

        {/* Time */}
        <div className="text-ink flex items-center gap-1.5 font-semibold tabular-nums">
          <Clock className="text-gold-600 h-3.5 w-3.5 shrink-0" />
          <span>{timeString}</span>
        </div>
      </div>
    </div>
  );
}

// ── Header Storefront Link Button ────────────────────────────────────────────

function StorefrontLinkButton() {
  return (
    <a
      href={STOREFRONT_URL}
      target="_blank"
      rel="noopener noreferrer"
      title="Open storefront in new tab to view live updates"
      className="via-gold-500 hover:via-gold-400 group relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 px-3.5 py-2 text-xs font-bold text-white shadow-md shadow-amber-500/20 transition-all duration-300 hover:scale-[1.02] hover:from-amber-400 hover:to-amber-500 hover:shadow-lg hover:shadow-amber-500/35 active:scale-[0.98]"
    >
      {/* Animated Light Reflection Effect on Hover */}
      <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />

      <Store className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:scale-110" />
      <span className="hidden whitespace-nowrap font-bold tracking-wide sm:inline">
        Visit Storefront
      </span>
      <span className="inline sm:hidden">Store</span>
      <ExternalLink className="h-3.5 w-3.5 shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </a>
  );
}

// ── Shell ────────────────────────────────────────────────────────────────────

export function AdminShell() {
  const { user, status, logout } = useAuthStore();
  const moduleToggles = useModulesStore((st) => st.toggles);
  const loadModules = useModulesStore((st) => st.load);
  useEffect(() => {
    if (status === 'authenticated') void loadModules();
  }, [status, loadModules]);
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');

  // Sign-out asks first, in the app's own dialog rather than the browser's.
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const handleLogout = () => setConfirmLogout(true);
  const doLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
      setConfirmLogout(false);
    }
  };

  // Live open-ticket count powering the Support → Ticket badge.
  const ticketsQ = useQuery({
    queryKey: ['nav-open-tickets-count'],
    queryFn: () => api.support.adminCount(),
    enabled: status === 'authenticated',
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const badges: Record<string, number> = {
    tickets: ticketsQ.data?.count ?? 0,
  };

  // Auto-open the groups that contain the current route.
  useEffect(() => {
    setOpen((prev) => {
      const next = new Set(prev);
      const visit = (entries: NavEntry[]) => {
        for (const e of entries) {
          if (isGroup(e)) {
            if (groupContainsPath(e, location.pathname)) next.add(e.key);
            visit(e.children);
          }
        }
      };
      NAV.forEach((s) => visit(s.items));
      return next;
    });
    setMobileOpen(false);
  }, [location.pathname]);

  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const pageTitle = useMemo(() => resolveTitle(location.pathname), [location.pathname]);
  const section = useMemo(() => resolveSection(location.pathname), [location.pathname]);

  // Search-filtered nav.
  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const sections = useMemo(() => {
    const visible = withoutHiddenModules(NAV, moduleToggles);
    if (!searching) return visible;
    return visible
      .map((s) => ({
        ...s,
        items: s.items.map((e) => filterEntry(e, q)).filter((e): e is NavEntry => e !== null),
      }))
      .filter((s) => s.items.length > 0);
  }, [q, searching, moduleToggles]);

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="bg-surface-subtle grid min-h-screen place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (status === 'anonymous' || !isAdmin(user)) {
    return <Navigate to="/login" replace />;
  }

  const sidebar = (
    <div className="bg-zone-900 flex h-full flex-col">
      {/* Logo */}
      <div className="relative flex items-center justify-center px-5 pb-3 pt-6">
        <img src="/image.png" alt="Terra Shoppy" className="h-22 w-60 object-contain" />
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="text-zone-400 absolute right-3 top-4 grid h-9 w-9 place-items-center rounded-lg transition-colors hover:bg-white/5 hover:text-white lg:hidden"
          aria-label="Close menu"
        >
          <X className="h-[18px] w-[18px]" />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 pb-2 pt-1">
        <div className="bg-zone-700/60 focus-within:border-gold-500/40 focus-within:bg-zone-700 flex items-center gap-2 rounded-xl border border-white/5 px-3.5 transition-colors duration-200">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search in menu"
            aria-label="Search in menu"
            className="placeholder:text-zone-400 h-10 w-full bg-transparent text-sm text-white outline-none"
          />
          <Search className="text-zone-400 h-4 w-4 shrink-0" strokeWidth={2} />
        </div>
      </div>

      {/* Navigation */}
      <nav className="ts-scroll-dark flex-1 space-y-5 overflow-y-auto px-3 py-3">
        {sections.length === 0 ? (
          <p className="text-zone-400 px-3 py-8 text-center text-sm">No menu items found.</p>
        ) : (
          sections.map((sec) => (
            <div key={sec.title}>
              <p className="text-zone-500 px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.16em]">
                {sec.title}
              </p>
              <div className="space-y-0.5">
                {sec.items.map((entry) => (
                  <NavTree
                    key={isGroup(entry) ? entry.key : entry.to}
                    entry={entry}
                    depth={0}
                    open={open}
                    toggle={toggle}
                    path={location.pathname}
                    searching={searching}
                    badges={badges}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </nav>
    </div>
  );

  return (
    <div className="bg-surface-subtle min-h-screen">
      <aside className="shadow-zone fixed inset-y-0 left-0 z-30 hidden w-[264px] lg:block">
        {sidebar}
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <motion.div
              className="bg-zone-950/70 absolute inset-0 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
              aria-hidden
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <motion.aside
              className="shadow-zone-lift absolute inset-y-0 left-0 w-[284px]"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 38 }}
            >
              {sidebar}
            </motion.aside>
          </div>
        )}
      </AnimatePresence>

      <div className="lg:pl-[264px]">
        <header className="border-border bg-surface/85 sticky top-0 z-20 flex h-[72px] items-center gap-4 border-b px-4 backdrop-blur-xl md:px-7">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="border-border text-ink-muted hover:bg-surface-subtle grid h-10 w-10 place-items-center rounded-xl border transition-colors lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-[18px] w-[18px]" />
          </button>

          <div className="min-w-0">
            <p className="text-ink-subtle flex items-center gap-1.5 text-[11px] font-semibold">
              <span className="text-gold-600">{section}</span>
              <ChevronRight className="h-3 w-3" />
              <span className="text-ink-muted">{pageTitle}</span>
            </p>
            <h1 className="text-ink truncate text-lg font-bold leading-tight tracking-tight">
              {pageTitle}
            </h1>
          </div>

          <div className="ml-auto flex items-center gap-2.5 md:gap-3">
            <LiveDateTimeWidget />
            <StorefrontLinkButton />
            <AlertsBell status={status} />
            <ProfileMenu user={user} onLogout={handleLogout} />
          </div>
        </header>

        <main
          className="ts-scroll relative min-h-[calc(100vh-72px)] overflow-x-hidden p-4 md:p-6 lg:p-8"
          style={{ backgroundColor: '#f8fafc' }}
        >
          {/* Subtle premium depth gradient */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-[300px] bg-gradient-to-b from-[#e2e8f0]/50 to-transparent" />

          <div className="relative z-10 mx-auto max-w-screen-2xl">
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>

      <ConfirmDialog
        open={confirmLogout}
        tone="danger"
        icon={<LogOut className="h-6 w-6" />}
        title="Log out?"
        message="Are you sure you want to log out of your account? You'll need to sign in again to continue."
        confirmLabel="Log out"
        cancelLabel="Stay signed in"
        loading={loggingOut}
        onConfirm={() => void doLogout()}
        onCancel={() => setConfirmLogout(false)}
      />
    </div>
  );
}
